import type {Instant, UUID} from './common'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

// ============================================================================
// Enums (matches Java SpanKind, SpanStatus, WriterStatus)
// ============================================================================

export const SpanKind = {
    HTTP_REQUEST: 'HTTP_REQUEST',
    CONTROLLER: 'CONTROLLER',
    SERVICE: 'SERVICE',
    SQL: 'SQL',
    EXTERNAL_HTTP: 'EXTERNAL_HTTP',
    FRONTEND_RENDER: 'FRONTEND_RENDER',
    FRONTEND_ACTION: 'FRONTEND_ACTION',
} as const;
export type SpanKind = typeof SpanKind[keyof typeof SpanKind]

export const SpanStatus = {
    OK: 'OK',
    ERROR: 'ERROR',
    CANCELLED: 'CANCELLED',
} as const;
export type SpanStatus = typeof SpanStatus[keyof typeof SpanStatus]

export const WriterStatus = {
    RUNNING: 'RUNNING',
    RESTARTING: 'RESTARTING',
    STOPPED: 'STOPPED',
} as const;
export type WriterStatus = typeof WriterStatus[keyof typeof WriterStatus]

// ============================================================================
// Span (matches SpanResponse)
// ============================================================================

export interface Span {
    spanId: UUID
    traceId: string
    parentSpanId: UUID | null
    kind: SpanKind
    operationName: string
    startedAt: Instant
    endedAt: Instant
    durationMs: number
    status: SpanStatus
    sessionId: string | null
    actionId: string | null
    correlationId: string | null
    e2eRunId: string | null
    e2eTestClass: string | null
    e2eTestMethod: string | null
    authUserId: UUID | null
    identityId: UUID | null
    impersonatorAuthUserId: UUID | null
    httpMethod: string | null
    httpUrl: string | null
    httpStatusCode: number | null
    httpResponseBytes: number | null
    sqlFingerprint: string | null
    sqlStatement: string | null
    sqlRowsAffected: number | null
    sqlPoolWaitMs: number | null
    errorCode: string | null
    errorMessage: string | null
    errorLogId: UUID | null
    attributes: Record<string, unknown> | null
}

// ============================================================================
// Trace (matches TraceResponse)
// ============================================================================

export interface Trace {
    sessionId: string
    e2eRunId: string | null
    e2eTestClass: string | null
    e2eTestMethod: string | null
    startedAt: Instant
    endedAt: Instant
    durationMs: number
    spanCount: number
    errorCount: number
    spans: Span[]
    truncated: boolean
}

// ============================================================================
// TraceListItem (matches TraceListItemResponse)
// ============================================================================

export interface TraceListItem {
    sessionId: string
    spanCount: number
    totalDurationMs: number
    errorCount: number
    earliestStartedAt: Instant
    latestStartedAt: Instant
    e2eRunId: string | null
    e2eTestClass: string | null
    e2eTestMethod: string | null
    userId: UUID | null
}

// ============================================================================
// SessionList (matches SessionListResponse)
// ============================================================================

export interface SessionList {
    sessionId: string
    earliestStartedAt: Instant
    latestStartedAt: Instant
    spanCount: number
    userId: UUID | null
    e2eTestClass: string | null
    e2eTestMethod: string | null
}

// ============================================================================
// Health (matches HealthResponse)
// ============================================================================

export interface Health {
    queueDepth: number
    dropCount: number
    lastWriteTimestamp: Instant | null
    writerStatus: WriterStatus
    crashCount: number
}

// ============================================================================
// FrontendSpanRequest (matches FrontendSpanRequest — POST body for span ingestion)
// ============================================================================

export interface FrontendSpanRequestSpan {
    kind: SpanKind
    operationName: string
    actionId: string
    startedAt: Instant
    durationMs: number
    attributes: Record<string, unknown> | null
}

export interface FrontendSpanRequest {
    sessionId: string
    spans: FrontendSpanRequestSpan[]
}
